// Archivo para la página "Editor de diseño"
//
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'

import {
  getLayouts,
  createLayout,
  updateLayout,
  deleteLayout,
  resolveLayoutsAuthContext,
} from '../../services/api'

import FieldsSidebar from '../../components/FieldsSidebar'
import LayoutDesignSurface from '../../components/canvas/LayoutDesignSurface'
import PropertiesSidebar from '../../components/properties/PropertiesSidebar'
import FileUpload from '../../utils/FileUpload'
import { CreateButton, ActionButtons } from '../../components/buttons'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import TuneIcon from '@mui/icons-material/Tune'
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar'
import { useTheme } from '@mui/material/styles'
import {
  parseLayoutConfig,
  normalizeFieldPosition,
} from '../../utils/layoutUtils'
import useAuth from '../../hooks/useAuth'
//import { AdminHero } from '../../components/settings/AdminConsoleShell'

const WORKFLOW_STAGE_BANNER_DEFAULT_FIELD = {
  id: 'palette_workflow_stage_banner',
  name: 'workflow_stage_banner',
  label: 'Banner de etapa del workflow',
  type: 'workflow_stage_banner',
  role: 'none',
  storage: 'workflow_resolver',
  editable: false,
  config: {
    presetId: 'process_default',
    resolverKey: 'workflow.current_stage',
    mode: 'progress_stage',
    prefix: 'ETAPA',
    shape: 'rounded',
    showStageNumber: true,
    showMetrics: true,
    showQuotes: false,
    emptyStateMode: 'message',
    emptyMessage: 'Sin etapa activa',
    customLabel: '',
    heading: '',
    title: '',
  },
}

/* ============================================================================
    Ejemplo de LayoutEditor que:

    1) Carga todos los layouts disponibles (getLayouts).
    2) Si existen layouts, muestra un <Select> para que el usuario elija el layout
       a editar/actualizar.
    3) Muestra botón "Crear nuevo layout" que limpia el estado para permitir crear
       uno desde cero.
    4) Muestra un botón "Actualizar layout" solo si hay un layout seleccionado.
    5) Muestra un botón "Crear layout" cuando no hay ninguno creado o cuando
       el usuario ha pulsado "Nuevo layout" (layoutId === null).
============================================================================ */

export default function LayoutEditor() {
  const navigate = useNavigate()
  const { productId } = useParams()
  const { loading } = useAuth()
  const theme = useTheme()
  const isMediumBuilder = useMediaQuery(theme.breakpoints.up('md'))
  const isDesktopBuilder = useMediaQuery(theme.breakpoints.up('lg'))

  // Lista completa de layouts disponibles
  const [layouts, setLayouts] = useState([])

  const [layoutContext, setLayoutContext] = useState({
    clientId: null,
    productId: null,
    isGlobalAdmin: false,
  })
  const [layoutContextReady, setLayoutContextReady] = useState(false)
  const [layoutContextError, setLayoutContextError] = useState('')

  // Función para validar los campos del layout antes de guardar
  const validateLayoutFields = () => {
    const namesSet = new Set()
    for (const field of layoutConfig) {
      if (
        !field.name ||
        typeof field.name !== 'string' ||
        field.name.trim() === ''
      ) {
        alert(
          `El campo con etiqueta "${
            field.label || 'sin etiqueta'
          }" no tiene un nombre técnico válido.`
        )
        return false
      }

      /*
      if (!field.name || field.name.trim() === '') {
        alert(
          `El campo con etiqueta "${
            field.label || 'sin etiqueta'
          }" no tiene un nombre interno (name) válido.`
        )
        return false
      }*/

      if (namesSet.has(field.name)) {
        alert(
          `El nombre interno (name) "${field.name}" está duplicado. Cada campo debe tener un nombre único.`
        )
        return false
      }

      namesSet.add(field.name)
    }
    return true
  }

  // Layout seleccionado para edición (si existe)
  const [layoutId, setLayoutId] = useState(null)
  const [layoutName, setLayoutName] = useState('')
  const [layoutConfig, setLayoutConfig] = useState([])

  const [availableFields, setAvailableFields] = useState([])
  const [selectedField, setSelectedField] = useState(null)

  const paletteFields = useMemo(() => {
    const normalizedAvailable = Array.isArray(availableFields)
      ? availableFields
      : []
    const hasWorkflowStageBanner = normalizedAvailable.some(
      (field) =>
        String(field?.type || '')
          .trim()
          .toLowerCase() === 'workflow_stage_banner' ||
        String(field?.name || '')
          .trim()
          .toLowerCase() === 'workflow_stage_banner'
    )

    return hasWorkflowStageBanner
      ? normalizedAvailable
      : [...normalizedAvailable, WORKFLOW_STAGE_BANNER_DEFAULT_FIELD]
  }, [availableFields])
  const [showGrid, setShowGrid] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [inspectorOpen, setInspectorOpen] = useState(false)

  const renderPalettePanel = () => (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, py: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Field palette
        </Typography>
      </Box>
      <Divider />
      <Box
        sx={{
          p: 2,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'hidden',
        }}
      >
        <FileUpload setFields={setAvailableFields}>
          <CreateButton entityType="field" onClick={() => {}} />
        </FileUpload>
        <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
          <FieldsSidebar fields={paletteFields} />
        </Box>
      </Box>
    </Box>
  )

  const renderInspectorPanel = () => (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, py: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Properties inspector
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
        <PropertiesSidebar
          selectedField={selectedField}
          onUpdate={(updatedField) => {
            setLayoutConfig((prev) =>
              prev.map((f) => (f.id === updatedField.id ? updatedField : f))
            )
            setSelectedField(updatedField)
          }}
        />
      </Box>
    </Box>
  )

  const normalizeEditorConfig = useCallback((rawConfig) => {
    let parsed = parseLayoutConfig(rawConfig)

    if (!Array.isArray(parsed) && typeof rawConfig === 'string') {
      try {
        const reparsed = JSON.parse(rawConfig)
        parsed = parseLayoutConfig(reparsed)
      } catch {
        parsed = []
      }
    }

    return (Array.isArray(parsed) ? parsed : []).map((field) =>
      normalizeFieldPosition(field)
    )
  }, [])

  const resolveLayoutConfigPayload = useCallback(
    (payload) => {
      if (Array.isArray(payload) || typeof payload === 'string') {
        return normalizeEditorConfig(payload)
      }

      if (payload && typeof payload === 'object') {
        return normalizeEditorConfig(
          payload.config ?? payload.layoutConfig ?? payload.fields ?? []
        )
      }

      return []
    },
    [normalizeEditorConfig]
  )

  const hasInMemoryEditableLayout = useMemo(
    () =>
      Boolean(layoutId) ||
      Boolean(String(layoutName || '').trim()) ||
      layoutConfig.length > 0,
    [layoutConfig.length, layoutId, layoutName]
  )

  const getApiErrorMessage = useCallback((error, fallbackMessage) => {
    const backendMessage = error?.response?.data?.error
    if (typeof backendMessage === 'string' && backendMessage.trim()) {
      return backendMessage
    }
    return fallbackMessage
  }, [])

  const buildLayoutRequestParams = useCallback((context) => {
    if (!context || !context.productId) {
      throw new Error('No hay contexto válido para layouts')
    }

    if (context.isGlobalAdmin) {
      if (!context.clientId) {
        throw new Error(
          'No se pudo resolver clientId para globalAdmin. Verifica el producto seleccionado.'
        )
      }

      return {
        clientId: context.clientId,
        productId: context.productId,
      }
    }

    return {
      productId: context.productId,
    }
  }, [])

  useEffect(() => {
    const loadLayouts = async () => {
      if (loading) return

      try {
        setLayoutContextReady(false)
        setLayoutContextError('')

        const context = await resolveLayoutsAuthContext({
          routeProductId: productId,
        })

        setLayoutContext(context)

        const params = buildLayoutRequestParams(context)
        const data = await getLayouts(params)

        setLayouts(Array.isArray(data) ? data : [])
        setLayoutContextReady(true)
      } catch (err) {
        console.error('Error loading layouts:', err)

        const resolvedMessage = getApiErrorMessage(
          err,
          'No se pudo resolver el contexto autenticado para cargar layouts'
        )

        setLayoutContextError(resolvedMessage)
        setLayouts([])
        setLayoutContextReady(false)
        alert(resolvedMessage)
      }
    }

    loadLayouts()
  }, [buildLayoutRequestParams, getApiErrorMessage, loading, productId])

  // Manejo de teclado para mover campos (igual que tu implementación)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedField) return

      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        e.preventDefault()
        const step = 1
        let dx = 0
        let dy = 0

        if (e.key === 'ArrowUp') dy = -step
        if (e.key === 'ArrowDown') dy = step
        if (e.key === 'ArrowLeft') dx = -step
        if (e.key === 'ArrowRight') dx = step

        setLayoutConfig((prev) =>
          prev.map((f) =>
            f.id === selectedField.id ? { ...f, x: f.x + dx, y: f.y + dy } : f
          )
        )
        setSelectedField((old) =>
          old ? { ...old, x: old.x + dx, y: old.y + dy } : null
        )
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedField])

  useEffect(() => {
    if (!selectedField) return

    const syncedField = layoutConfig.find(
      (field) => field.id === selectedField.id
    )

    if (!syncedField) {
      setSelectedField(null)
      return
    }

    if (syncedField !== selectedField) {
      setSelectedField(syncedField)
    }
  }, [layoutConfig, selectedField])

  // --------------------------------------------------------------------------
  // SEPARACIÓN DE LÓGICAS: CREAR VS. ACTUALIZAR
  // --------------------------------------------------------------------------

  /**
   * handleSelectLayout:
   * Carga en el estado el layout seleccionado para edición.
   */
  const handleSelectLayout = (id) => {
    const layout = layouts.find((l) => l.id === id)
    if (layout) {
      setLayoutId(layout.id)
      setLayoutName(layout.name || '')
      setLayoutConfig(resolveLayoutConfigPayload(layout))
      setSelectedField(null)
    }
  }

  /**
   * handleCreateNewLayout:
   * Limpia el estado para permitir crear un layout desde cero,
   * sin layoutId asignado.
   */
  const handleCreateNewLayout = () => {
    setLayoutId(null)
    setLayoutName('')
    setLayoutConfig([])
    setSelectedField(null)
  }

  /**
   * handleCreateLayout:
   * Lógica de POST /layouts/register
   */
  const handleCreateLayout = async () => {
    if (!layoutContextReady) {
      alert(
        layoutContextError ||
          'El contexto autenticado aún no está listo. Espera unos segundos e inténtalo nuevamente.'
      )
      return
    }

    if (!validateLayoutFields()) return

    try {
      const payload = {
        name: layoutName,
        config: layoutConfig,
      }
      const createParams = buildLayoutRequestParams(layoutContext)

      const newLayout = await createLayout(payload, createParams)
      alert('Layout creado exitosamente!')

      // Agregamos el nuevo layout a la lista
      setLayouts((prev) => [...prev, newLayout])

      // Seleccionamos el layout recién creado para edición posterior
      setLayoutId(newLayout.id)
      // Aseguramos reflejar datos
      setLayoutName(newLayout.name)
      setLayoutConfig(resolveLayoutConfigPayload(newLayout))
      setSelectedField(null)
    } catch (error) {
      console.error('Error creando layout:', error)
      alert(getApiErrorMessage(error, 'Error al crear layout'))
    }
  }

  /**
   * handleUpdateLayout:
   * Lógica de PUT /layouts/:id
   */
  const handleUpdateLayout = async () => {
    if (!layoutId) {
      alert('No has seleccionado ningún layout para actualizar')
      return
    }

    if (!validateLayoutFields()) return

    // 1) Sanear sólo las propiedades que esperamos
    const sanitizedConfig = layoutConfig.map(
      ({
        id,
        name,
        type,
        label,
        role,
        storage,
        editable,
        x,
        y,
        width,
        height,
        fontSize,
        config,
      }) => ({
        id,
        name,
        type,
        label,
        role,
        storage,
        editable,
        x,
        y,
        width,
        height,
        fontSize,
        ...(config && typeof config === 'object' ? { config } : {}),
      })
    )

    // 2) Validar rangos básicos
    for (const f of sanitizedConfig) {
      if (f.x < 0 || f.y < 0) {
        return alert(
          `Las coordenadas de "${f.label}" deben ser ≥ 0 (x=${f.x}, y=${f.y})`
        )
      }
      const MAX_WIDTH = 800 // ajusta según tu canvas
      const MAX_HEIGHT = 1200 // ajusta según tu canvas
      if (f.width > MAX_WIDTH || f.height > MAX_HEIGHT) {
        return alert(
          `Las dimensiones de "${f.label}" exceden el límite (${MAX_WIDTH}×${MAX_HEIGHT}).`
        )
      }
    }

    // 3) Enviar sólo name * config saneada
    try {
      const payload = {
        name: layoutName,
        config: sanitizedConfig,
      }
      const updated = await updateLayout(layoutId, payload)
      alert('Layout actualizado exitosamente!')

      // Actualizamos la lista local con los datos actualizados
      setLayouts((prev) =>
        prev.map((l) => (l.id === layoutId ? { ...l, ...updated } : l))
      )
    } catch (error) {
      console.error('Error actualizando layout:', error)
      alert(getApiErrorMessage(error, 'Error al actualizar layout'))
    }
  }

  /**
   * handleDeleteLayout:
   * Lógica de DELETE /layouts/:id
   */
  const handleDeleteLayout = async () => {
    if (!layoutId) {
      alert('No hay un layout seleccionado para eliminar')
      return
    }
    try {
      await deleteLayout(layoutId)
      alert('Layout eliminado exitosamente!')

      // Quitamos layout de la lista
      setLayouts((prev) => prev.filter((l) => l.id !== layoutId))

      // Reset de la selección local
      setLayoutId(null)
      setLayoutName('')
      setLayoutConfig([])
      setSelectedField(null)
    } catch (error) {
      console.error('Error deleting layout:', error)
      alert(getApiErrorMessage(error, 'Error al eliminar layout'))
    }
  }

  // --------------------------------------------------------------------------
  // IMPORTAR / EXPORTAR JSON
  // --------------------------------------------------------------------------

  const exportLayoutAsJSON = () => {
    const exportData = {
      productId,
      layoutName,
      fields: layoutConfig,
    }
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${
      layoutName.replace(/\s+/g, '_') || 'layout'
    }_${productId}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const importLayoutFromJSON = (event) => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result)
        const importedConfig = resolveLayoutConfigPayload(json)

        if (importedConfig.length > 0) {
          setLayoutId(null)
          setLayoutName(json.layoutName || 'Layout importado')
          setLayoutConfig(importedConfig)
          setSelectedField(null)
          alert('Layout importado correctamente')
        } else {
          alert('El archivo no tiene un formato válido')
        }
      } catch (err) {
        alert('Error al leer el archivo JSON')
        console.error(err)
      }
    }
    reader.readAsText(file)
  }

  // --------------------------------------------------------------------------
  // RENDERIZADO
  // --------------------------------------------------------------------------

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.default',
          flex: 1,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flex: '0 0 auto',
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction={{ xs: 'column', xl: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', xl: 'center' }}
              justifyContent="space-between"
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Layout Builder
                </Typography>
                <Chip
                  size="small"
                  label={layoutId ? `Editando #${layoutId}` : 'Nuevo layout'}
                  color={layoutId ? 'primary' : 'default'}
                  variant={layoutId ? 'filled' : 'outlined'}
                />
                <Chip
                  size="small"
                  label={`${layoutConfig.length} campo(s)`}
                  variant="outlined"
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {!isMediumBuilder ? (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<ViewSidebarIcon />}
                      onClick={() => setPaletteOpen(true)}
                    >
                      Campos
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<TuneIcon />}
                      onClick={() => setInspectorOpen(true)}
                    >
                      Propiedades
                    </Button>
                  </>
                ) : null}
                <Button
                  variant="text"
                  onClick={() =>
                    navigate('/settings/controlpanel/layout-manager')
                  }
                >
                  Volver a asignaciones
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowGrid(!showGrid)}
                >
                  {showGrid ? 'Ocultar cuadrícula' : 'Mostrar cuadrícula'}
                </Button>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              sx={{ flexWrap: 'wrap' }}
            >
              {layouts.length > 0 ? (
                <Select
                  value={layoutId || ''}
                  onChange={(e) => handleSelectLayout(e.target.value)}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: { xs: '100%', md: 240 } }}
                >
                  <MenuItem value="" disabled>
                    -- Selecciona un Layout --
                  </MenuItem>
                  {layouts.map((layout) => (
                    <MenuItem key={layout.id} value={layout.id}>
                      {layout.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}

              <TextField
                fullWidth
                size="small"
                label="Nombre del Layout"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
                sx={{ minWidth: { lg: 260 }, flex: { lg: 1 } }}
              />

              {layouts.length > 0 ? (
                <Button variant="outlined" onClick={handleCreateNewLayout}>
                  Nuevo
                </Button>
              ) : !hasInMemoryEditableLayout ? (
                <Chip
                  size="small"
                  label="Sin layouts guardados"
                  variant="outlined"
                />
              ) : null}

              {layoutId ? (
                <>
                  <Button variant="contained" onClick={handleUpdateLayout}>
                    Actualizar
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteLayout}
                  >
                    Eliminar
                  </Button>
                </>
              ) : layouts.length === 0 ? (
                <Button
                  variant="contained"
                  onClick={handleCreateLayout}
                  disabled={!layoutContextReady}
                >
                  Crear primer layout
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleCreateLayout}
                  disabled={!layoutContextReady}
                >
                  Crear
                </Button>
              )}

              {!layoutContextReady ? (
                <Chip
                  size="small"
                  color={layoutContextError ? 'error' : 'default'}
                  variant="outlined"
                  label={
                    layoutContextError ||
                    'Resolviendo contexto autenticado para layouts...'
                  }
                />
              ) : null}

              <Button variant="outlined" onClick={exportLayoutAsJSON}>
                Exportar
              </Button>

              <Button variant="outlined" component="label">
                Importar
                <input
                  type="file"
                  accept=".json"
                  hidden
                  onChange={importLayoutFromJSON}
                />
              </Button>

              <Box sx={{ ml: { lg: 'auto' } }}>
                <ActionButtons />
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            px: { xs: 2, md: 3 },
            py: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: isDesktopBuilder
                ? 'clamp(240px, 18vw, 300px) minmax(420px, 1fr) clamp(280px, 20vw, 340px)'
                : isMediumBuilder
                  ? 'clamp(200px, 20vw, 240px) minmax(360px, 1fr) clamp(240px, 24vw, 280px)'
                  : 'minmax(0, 1fr)',
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {isMediumBuilder ? renderPalettePanel() : null}

            <Box
              sx={{
                minHeight: 0,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Design surface
                </Typography>
                {!isMediumBuilder ? (
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => setPaletteOpen(true)}
                      aria-label="Abrir panel de campos"
                    >
                      <ViewSidebarIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setInspectorOpen(true)}
                      aria-label="Abrir panel de propiedades"
                    >
                      <MenuOpenIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : null}
              </Box>
              <Divider />
              <Box
                sx={{
                  p: { xs: 1.5, md: 2 },
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <LayoutDesignSurface
                  layoutConfig={layoutConfig}
                  setLayoutConfig={setLayoutConfig}
                  selectedField={selectedField}
                  setSelectedField={setSelectedField}
                  showGrid={showGrid}
                />

                {!isMediumBuilder ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      bottom: 16,
                      zIndex: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<ViewSidebarIcon />}
                      onClick={() => setPaletteOpen(true)}
                    >
                      CAMPOS
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<TuneIcon />}
                      onClick={() => setInspectorOpen(true)}
                    >
                      PROPIEDADES
                    </Button>
                  </Stack>
                ) : null}
              </Box>
            </Box>

            {isMediumBuilder ? renderInspectorPanel() : null}
          </Box>
        </Box>

        <Drawer
          anchor="left"
          open={!isMediumBuilder && paletteOpen}
          onClose={() => setPaletteOpen(false)}
          PaperProps={{
            sx: {
              width: 'min(88vw, 320px)',
              p: 1.5,
              bgcolor: 'background.default',
            },
          }}
        >
          <Box sx={{ height: '100%', minHeight: 0 }}>
            {renderPalettePanel()}
          </Box>
        </Drawer>

        <Drawer
          anchor="right"
          open={!isMediumBuilder && inspectorOpen}
          onClose={() => setInspectorOpen(false)}
          PaperProps={{
            sx: {
              width: 'min(92vw, 360px)',
              p: 1.5,
              bgcolor: 'background.default',
            },
          }}
        >
          <Box sx={{ height: '100%', minHeight: 0 }}>
            {renderInspectorPanel()}
          </Box>
        </Drawer>
      </Box>
    </DndProvider>
  )
}
